import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { formatINR, formatDate } from '../../lib/format';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import {
  Banknote, Landmark, Plus, X, CheckCircle2, Ban, Search, ArrowDownCircle,
  ArrowUpCircle, FileText, Star, RefreshCw, ChevronLeft, ChevronRight,
} from 'lucide-react';

const inp = 'w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]';
const lbl = 'block text-[10px] font-display font-bold uppercase tracking-[0.14em] text-slate-500 mb-1';

const STATUS_BADGE = {
  draft: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  void: 'bg-slate-100 text-slate-500 border-slate-200 line-through',
};

const StatusChip = ({ s }) => (
  <span className={`inline-block px-2 py-0.5 text-[10px] font-display font-bold uppercase border rounded-sm ${STATUS_BADGE[s] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>{s}</span>
);

// ─── Vendor search picker ────────────────────────────────────────────────────
function VendorPicker({ value, onPick }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      apiGet('/vendors', { q, limit: 10 }).then((d) => {
        setResults((d?.items || d || []).slice(0, 10));
        setOpen(true);
      }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <label className={lbl}>Payee (vendor search)</label>
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="payment-vendor-search"
          className={`${inp} pl-8`}
          placeholder={value?.name || 'Search vendor…'}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-lg max-h-56 overflow-auto">
          {results.map((v) => (
            <button
              key={v.id}
              type="button"
              data-testid={`vendor-option-${v.vendor_code || v.id}`}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100"
              onClick={() => { onPick(v); setQ(''); setOpen(false); }}
            >
              <span className="font-bold">{v.name}</span>
              <span className="text-[11px] text-slate-500 ml-2">{v.vendor_code}</span>
            </button>
          ))}
        </div>
      )}
      {value?.name && <div className="text-[11px] text-emerald-700 mt-1" data-testid="payment-vendor-picked">Selected: {value.name}</div>}
    </div>
  );
}

// ─── Bank select with available headroom ────────────────────────────────────
function BankSelect({ banks, value, onChange, testId }) {
  return (
    <div>
      <label className={lbl}>Bank Account</label>
      <select data-testid={testId} className={inp} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">— select bank —</option>
        {banks.map((b) => (
          <option key={b.id} value={b.id}>
            {b.account_name} · avail {formatINR(b.available)}
          </option>
        ))}
      </select>
    </div>
  );
}

// ─── Allocation table (invoices / bills) ────────────────────────────────────
function AllocationSection({ rows, selected, setSelected, docLabel, testPrefix }) {
  const toggle = (r, remaining) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[r.id]) delete next[r.id];
      else next[r.id] = { doc_no: r.doc_no, amount: remaining };
      return next;
    });
  };
  const setAmt = (id, amt) => setSelected((prev) => ({ ...prev, [id]: { ...prev[id], amount: amt } }));
  if (!rows.length) return <div className="text-xs text-slate-500 py-2" data-testid={`${testPrefix}-alloc-empty`}>No outstanding {docLabel} found.</div>;
  return (
    <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-52 overflow-auto">
      {rows.map((r) => {
        const sel = selected[r.id];
        return (
          <div key={r.id} className="flex items-center gap-2 px-2 py-1.5 text-xs" data-testid={`${testPrefix}-alloc-row-${r.doc_no}`}>
            <input type="checkbox" checked={!!sel} onChange={() => toggle(r, r.remaining)} data-testid={`${testPrefix}-alloc-check-${r.doc_no}`} />
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{r.doc_no}</div>
              <div className="text-[10px] text-slate-500">net {formatINR(r.net)} · remaining {formatINR(r.remaining)}</div>
            </div>
            <input
              type="number"
              className="w-28 border border-slate-300 rounded-sm px-2 py-1 text-xs text-right disabled:bg-slate-50"
              value={sel ? sel.amount : ''}
              disabled={!sel}
              onChange={(e) => setAmt(r.id, e.target.value)}
              data-testid={`${testPrefix}-alloc-amt-${r.doc_no}`}
            />
          </div>
        );
      })}
    </div>
  );
}

// ─── New Payment drawer ──────────────────────────────────────────────────────
function PaymentDrawer({ banks, onClose, onSaved }) {
  const [form, setForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10), bank_account_id: '',
    mode: 'neft', instrument_ref: '', payee_type: 'vendor', payee_name: '',
    amount: '', purpose: 'invoice_payment', tds_deducted: 0, narration: '',
  });
  const [vendor, setVendor] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState({});
  const [saving, setSaving] = useState(false);
  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!vendor?.id) { setInvoices([]); return; }
    apiGet('/vendor-invoices', { vendor_id: vendor.id, match_status: 'approved', limit: 100 }).then((d) => {
      setInvoices((d?.items || []).map((i) => ({
        id: i.id, doc_no: i.invoice_no,
        net: i.net_payable || i.gross_amount || 0,
        remaining: Math.max(0, (i.net_payable || i.gross_amount || 0) - (i.partially_paid_amount || 0)),
      })).filter((i) => i.remaining > 0));
    }).catch(() => {});
  }, [vendor]);

  const allocTotal = useMemo(() => Object.values(selected).reduce((s, a) => s + Number(a.amount || 0), 0), [selected]);
  const amountNum = Number(form.amount || 0);
  const allocMismatch = form.purpose === 'invoice_payment' && Math.abs(allocTotal - amountNum) > 0.01;

  const save = async () => {
    if (!form.bank_account_id) return toast.error('Select a bank account');
    if (!form.payee_name && !vendor?.name) return toast.error('Payee is required');
    if (amountNum <= 0) return toast.error('Amount must be positive');
    if (allocMismatch) return toast.error(`Σ allocations (${formatINR(allocTotal)}) must equal amount (${formatINR(amountNum)})`);
    setSaving(true);
    try {
      await apiPost('/finance/payments', {
        ...form,
        amount: amountNum,
        tds_deducted: Number(form.tds_deducted || 0),
        vendor_id: vendor?.id || null,
        payee_name: form.payee_name || vendor?.name || '',
        allocations: Object.entries(selected).map(([id, a]) => ({
          doc_type: 'vendor_invoice', doc_id: id, doc_no: a.doc_no, allocated_amount: Number(a.amount || 0),
        })),
      });
      toast.success('Payment draft created');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create payment');
    } finally { setSaving(false); }
  };

  return (
    <Drawer title="New Payment Voucher" onClose={onClose} testId="payment-drawer">
      <div className="grid grid-cols-2 gap-3">
        <div><label className={lbl}>Date</label><input type="date" className={inp} value={form.payment_date} onChange={(e) => u('payment_date', e.target.value)} data-testid="payment-date" /></div>
        <div><label className={lbl}>Mode</label>
          <select className={inp} value={form.mode} onChange={(e) => u('mode', e.target.value)} data-testid="payment-mode">
            {['neft', 'rtgs', 'imps', 'cheque', 'upi', 'cash'].map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
          </select>
        </div>
        <div className="col-span-2"><BankSelect banks={banks} value={form.bank_account_id} onChange={(v) => u('bank_account_id', v)} testId="payment-bank-select" /></div>
        <div><label className={lbl}>Payee Type</label>
          <select className={inp} value={form.payee_type} onChange={(e) => u('payee_type', e.target.value)} data-testid="payment-payee-type">
            {['vendor', 'subcontractor', 'employee', 'statutory', 'other'].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div><label className={lbl}>Purpose</label>
          <select className={inp} value={form.purpose} onChange={(e) => u('purpose', e.target.value)} data-testid="payment-purpose">
            {['invoice_payment', 'advance', 'statutory_payment', 'other'].map((p) => <option key={p} value={p}>{p.replace('_', ' ')}</option>)}
          </select>
        </div>
        {['vendor', 'subcontractor'].includes(form.payee_type) ? (
          <div className="col-span-2"><VendorPicker value={vendor} onPick={(v) => { setVendor(v); u('payee_name', v.name); }} /></div>
        ) : (
          <div className="col-span-2"><label className={lbl}>Payee Name</label><input className={inp} value={form.payee_name} onChange={(e) => u('payee_name', e.target.value)} data-testid="payment-payee-name" /></div>
        )}
        <div><label className={lbl}>Amount (₹)</label><input type="number" className={inp} value={form.amount} onChange={(e) => u('amount', e.target.value)} data-testid="payment-amount" /></div>
        <div><label className={lbl}>Instrument Ref (UTR / Cheque)</label><input className={inp} value={form.instrument_ref} onChange={(e) => u('instrument_ref', e.target.value)} data-testid="payment-instrument" /></div>
        <div><label className={lbl}>TDS Deducted (₹)</label><input type="number" className={inp} value={form.tds_deducted} onChange={(e) => u('tds_deducted', e.target.value)} data-testid="payment-tds" /></div>
        <div className="col-span-2"><label className={lbl}>Narration</label><input className={inp} value={form.narration} onChange={(e) => u('narration', e.target.value)} data-testid="payment-narration" /></div>
      </div>

      {form.purpose === 'invoice_payment' && (
        <div className="mt-4">
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">
            Settle approved invoices {vendor ? `— ${vendor.name}` : '(pick a vendor)'}
          </div>
          <AllocationSection rows={invoices} selected={selected} setSelected={setSelected} docLabel="approved-unpaid invoices" testPrefix="payment" />
          <div className={`text-xs mt-1 font-bold ${allocMismatch ? 'text-rose-600' : 'text-emerald-700'}`} data-testid="payment-alloc-total">
            Σ allocations: {formatINR(allocTotal)} {allocMismatch ? `≠ amount ${formatINR(amountNum)}` : '✓'}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 mt-5">
        <button className="px-4 py-2 text-xs font-display font-bold uppercase border border-slate-300 rounded-sm" onClick={onClose} data-testid="payment-cancel">Cancel</button>
        <button className="px-4 py-2 text-xs font-display font-bold uppercase text-white bg-[#1F7A6E] rounded-sm disabled:opacity-40" disabled={saving} onClick={save} data-testid="payment-save">
          {saving ? 'Saving…' : 'Save Draft'}
        </button>
      </div>
    </Drawer>
  );
}

// ─── New Receipt drawer ──────────────────────────────────────────────────────
function ReceiptDrawer({ banks, onClose, onSaved }) {
  const [form, setForm] = useState({
    receipt_date: new Date().toISOString().slice(0, 10), bank_account_id: '',
    mode: 'neft', instrument_ref: '', payer_type: 'client', client_name: '', client_pan: '',
    amount: '', purpose: 'other', client_tds_deducted: 0, narration: '',
  });
  const [saving, setSaving] = useState(false);
  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const amountNum = Number(form.amount || 0);

  const save = async () => {
    if (!form.bank_account_id) return toast.error('Select a bank account');
    if (!form.client_name) return toast.error('Client name is required');
    if (amountNum <= 0) return toast.error('Amount must be positive');
    setSaving(true);
    try {
      await apiPost('/finance/receipts', {
        ...form,
        amount: amountNum,
        client_tds_deducted: Number(form.client_tds_deducted || 0),
      });
      toast.success('Receipt draft created');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create receipt');
    } finally { setSaving(false); }
  };

  return (
    <Drawer title="New Receipt Voucher" onClose={onClose} testId="receipt-drawer">
      <div className="grid grid-cols-2 gap-3">
        <div><label className={lbl}>Date</label><input type="date" className={inp} value={form.receipt_date} onChange={(e) => u('receipt_date', e.target.value)} data-testid="receipt-date" /></div>
        <div><label className={lbl}>Mode</label>
          <select className={inp} value={form.mode} onChange={(e) => u('mode', e.target.value)} data-testid="receipt-mode">
            {['neft', 'rtgs', 'cheque', 'upi', 'cash'].map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
          </select>
        </div>
        <div className="col-span-2"><BankSelect banks={banks} value={form.bank_account_id} onChange={(v) => u('bank_account_id', v)} testId="receipt-bank-select" /></div>
        <div><label className={lbl}>Client Name</label><input className={inp} value={form.client_name} onChange={(e) => u('client_name', e.target.value)} data-testid="receipt-client-name" /></div>
        <div><label className={lbl}>Client PAN (for TCS)</label><input className={inp} value={form.client_pan} onChange={(e) => u('client_pan', e.target.value.toUpperCase())} placeholder="Optional — affects TCS rate" data-testid="receipt-client-pan" /></div>
        <div><label className={lbl}>Purpose</label>
          <select className={inp} value={form.purpose} onChange={(e) => u('purpose', e.target.value)} data-testid="receipt-purpose">
            {['invoice_collection', 'advance', 'other'].map((p) => <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
        <div><label className={lbl}>Amount Credited (₹)</label><input type="number" className={inp} value={form.amount} onChange={(e) => u('amount', e.target.value)} data-testid="receipt-amount" /></div>
        <div><label className={lbl}>Client TDS Deducted (₹)</label><input type="number" className={inp} value={form.client_tds_deducted} onChange={(e) => u('client_tds_deducted', e.target.value)} data-testid="receipt-client-tds" /></div>
        <div><label className={lbl}>Instrument Ref (UTR)</label><input className={inp} value={form.instrument_ref} onChange={(e) => u('instrument_ref', e.target.value)} data-testid="receipt-instrument" /></div>
        <div className="col-span-2"><label className={lbl}>Narration</label><input className={inp} value={form.narration} onChange={(e) => u('narration', e.target.value)} data-testid="receipt-narration" /></div>
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <button className="px-4 py-2 text-xs font-display font-bold uppercase border border-slate-300 rounded-sm" onClick={onClose} data-testid="receipt-cancel">Cancel</button>
        <button className="px-4 py-2 text-xs font-display font-bold uppercase text-white bg-[#1F7A6E] rounded-sm disabled:opacity-40" disabled={saving} onClick={save} data-testid="receipt-save">
          {saving ? 'Saving…' : 'Save Draft'}
        </button>
      </div>
    </Drawer>
  );
}

// ─── Generic right drawer shell ─────────────────────────────────────────────
function Drawer({ title, onClose, children, testId, wide }) {
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" data-testid={testId}>
      <div className={`bg-white h-full ${wide ? 'w-[680px]' : 'w-[520px]'} max-w-full overflow-y-auto p-5 shadow-2xl`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-extrabold">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid={`${testId}-close`}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Voucher table (shared by payments & receipts) ──────────────────────────
function VoucherTable({ kind, refreshKey, banksReload }) {
  const { user } = useAuth() || {};
  const me = user?.user_id;
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [statusF, setStatusF] = useState('');
  const base = kind === 'payment' ? '/finance/payments' : '/finance/receipts';

  const load = useCallback(() => {
    apiGet(base, { page, ...(statusF ? { status: statusF } : {}) }).then(setData).catch(() => {});
  }, [base, page, statusF]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const approve = async (v) => {
    try {
      await apiPost(`${base}/${v.id}/approve`, {});
      toast.success(`${v.voucher_no} approved — bank balance updated`);
      load(); banksReload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
  };
  const voidIt = async (v) => {
    const reason = window.prompt(`Void ${v.voucher_no}? Enter a reason:`);
    if (!reason) return;
    try {
      await apiPost(`${base}/${v.id}/void`, { reason });
      toast.success(`${v.voucher_no} voided${v.status === 'approved' ? ' — bank balance reversed' : ''}`);
      load(); banksReload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Void failed'); }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <select className="border border-slate-300 rounded-sm px-2 py-1.5 text-xs" value={statusF} onChange={(e) => { setStatusF(e.target.value); setPage(1); }} data-testid={`${kind}-status-filter`}>
          <option value="">All statuses</option>
          {['draft', 'approved', 'void'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={load} className="text-slate-500 hover:text-[#1F7A6E]" data-testid={`${kind}-refresh`}><RefreshCw size={14} /></button>
        <div className="ml-auto text-xs text-slate-500">{data.total} vouchers</div>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Voucher No</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">{kind === 'payment' ? 'Payee' : 'Client'}</th>
              <th className="px-3 py-2 text-left">Bank</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-left">Mode</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-slate-500" data-testid={`${kind}-empty`}>No {kind} vouchers yet.</td></tr>
            )}
            {data.items.map((v) => (
              <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`${kind}-row-${v.voucher_no}`}>
                <td className="px-3 py-2 font-bold">
                  {v.voucher_no}
                  {v.source === 'legacy_mark_paid' && (
                    <span className="ml-2 px-1.5 py-0.5 text-[9px] font-display font-bold uppercase bg-violet-50 text-violet-700 border border-violet-200 rounded-sm" data-testid={`${kind}-legacy-tag-${v.voucher_no}`}>auto (legacy)</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">{formatDate(v.payment_date || v.receipt_date)}</td>
                <td className="px-3 py-2">{v.payee_name || v.client_name}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{v.bank_account_name}</td>
                <td className="px-3 py-2 text-right font-display font-bold">{formatINR(v.amount)}</td>
                <td className="px-3 py-2 text-xs uppercase">{v.mode}</td>
                <td className="px-3 py-2"><StatusChip s={v.status} /></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {v.status === 'draft' && v.created_by_id !== me && (
                    <button onClick={() => approve(v)} className="text-emerald-600 hover:text-emerald-800 mr-2" title="Approve (SoD enforced)" data-testid={`${kind}-approve-${v.voucher_no}`}><CheckCircle2 size={15} /></button>
                  )}
                  {v.status === 'draft' && v.created_by_id === me && (
                    <span className="text-[9px] text-slate-400 mr-2" title="Segregation of duties — creator cannot approve" data-testid={`${kind}-sod-note-${v.voucher_no}`}>awaits other approver</span>
                  )}
                  {['draft', 'approved'].includes(v.status) && (
                    <button onClick={() => voidIt(v)} className="text-rose-500 hover:text-rose-700" title="Void voucher" data-testid={`${kind}-void-${v.voucher_no}`}><Ban size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.pages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-2 text-xs">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1 disabled:opacity-30" data-testid={`${kind}-prev`}><ChevronLeft size={14} /></button>
          <span>Page {page} / {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="p-1 disabled:opacity-30" data-testid={`${kind}-next`}><ChevronRight size={14} /></button>
        </div>
      )}
    </div>
  );
}

// ─── Bank Accounts tab ───────────────────────────────────────────────────────
function BankTab({ banks, reload }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statementAcc, setStatementAcc] = useState(null);
  const [statement, setStatement] = useState(null);

  const openStatement = async (acc) => {
    setStatementAcc(acc); setStatement(null);
    try { setStatement(await apiGet(`/finance/bank-accounts/${acc.id}/statement`)); }
    catch (e) { toast.error('Failed to load statement'); setStatementAcc(null); }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold uppercase text-white bg-[#1F7A6E] rounded-sm" data-testid="bank-add-btn">
          <Plus size={13} /> Add Bank Account
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {banks.length === 0 && <div className="col-span-3 text-center py-10 text-slate-500" data-testid="bank-empty">No bank accounts yet. Add your first account to start recording payments & receipts.</div>}
        {banks.map((b) => (
          <div key={b.id} className={`border rounded-sm p-4 ${b.is_active === false ? 'opacity-50 border-slate-200' : 'border-slate-300'}`} data-testid={`bank-card-${b.account_name}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-extrabold text-sm flex items-center gap-1.5">
                  <Landmark size={14} className="text-[#1F7A6E]" /> {b.account_name}
                  {b.is_default && <Star size={12} className="text-amber-500 fill-amber-400" title="Default account" />}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">{b.bank_name} · {b.branch || '—'} · <span className="uppercase font-bold">{b.account_type}</span></div>
              </div>
            </div>
            <div className="mt-3 flex items-end justify-between">
              <div>
                <div className="text-[10px] uppercase font-bold text-slate-400">Balance</div>
                <div className={`font-display text-lg font-extrabold ${(b.current_balance || 0) < 0 ? 'text-rose-600' : 'text-[#1F7A6E]'}`} data-testid={`bank-balance-${b.account_name}`}>{formatINR(b.current_balance)}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase font-bold text-slate-400">{b.account_type === 'current' ? 'Available' : 'Headroom'}</div>
                <div className="font-display text-sm font-bold text-emerald-700" data-testid={`bank-available-${b.account_name}`}>{formatINR(b.available)}</div>
              </div>
            </div>
            <div className="flex gap-2 mt-3 pt-2 border-t border-slate-100">
              <button onClick={() => openStatement(b)} className="text-[11px] font-bold text-[#1F7A6E] hover:underline flex items-center gap-1" data-testid={`bank-statement-${b.account_name}`}><FileText size={11} /> Statement</button>
              <button onClick={() => { setEditing(b); setShowForm(true); }} className="text-[11px] font-bold text-slate-500 hover:underline ml-auto" data-testid={`bank-edit-${b.account_name}`}>Edit</button>
            </div>
          </div>
        ))}
      </div>

      {showForm && <BankForm editing={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); reload(); }} />}

      {statementAcc && (
        <Drawer title={`Statement — ${statementAcc.account_name}`} onClose={() => setStatementAcc(null)} testId="bank-statement-drawer" wide>
          {!statement ? <div className="text-slate-500 text-sm">Loading…</div> : (
            <div>
              <div className="text-xs text-slate-500 mb-2">Opening {formatINR(statement.opening_balance)} → Closing <span className="font-bold">{formatINR(statement.closing_balance)}</span></div>
              <table className="w-full text-xs border border-slate-200">
                <thead className="bg-slate-50 text-[10px] font-bold uppercase text-slate-500">
                  <tr><th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-left">Voucher</th><th className="px-2 py-1.5 text-left">Party</th><th className="px-2 py-1.5 text-right">Debit</th><th className="px-2 py-1.5 text-right">Credit</th><th className="px-2 py-1.5 text-right">Balance</th></tr>
                </thead>
                <tbody>
                  {(statement.rows || []).length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-400">No transactions yet.</td></tr>}
                  {(statement.rows || []).map((r, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`stmt-row-${r.voucher_no}`}>
                      <td className="px-2 py-1.5">{formatDate(r.date)}</td>
                      <td className="px-2 py-1.5 font-bold">{r.voucher_no}</td>
                      <td className="px-2 py-1.5">{r.counterparty}</td>
                      <td className="px-2 py-1.5 text-right text-rose-600">{r.debit ? formatINR(r.debit) : ''}</td>
                      <td className="px-2 py-1.5 text-right text-emerald-700">{r.credit ? formatINR(r.credit) : ''}</td>
                      <td className="px-2 py-1.5 text-right font-bold">{formatINR(r.running_balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Drawer>
      )}
    </div>
  );
}

function BankForm({ editing, onClose, onSaved }) {
  const [f, setF] = useState(editing || {
    account_name: '', bank_name: '', branch: '', account_number: '', ifsc: '',
    account_type: 'current', sanctioned_limit: 0, current_balance: 0, is_default: false, is_active: true,
  });
  const u = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    if (!f.account_name || !f.bank_name) return toast.error('Account name and bank name are required');
    try {
      const body = { ...f, sanctioned_limit: Number(f.sanctioned_limit || 0), current_balance: Number(f.current_balance || 0) };
      if (editing) await apiPut(`/finance/bank-accounts/${editing.id}`, body);
      else await apiPost('/finance/bank-accounts', body);
      toast.success(editing ? 'Account updated' : 'Account created');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  return (
    <Drawer title={editing ? 'Edit Bank Account' : 'Add Bank Account'} onClose={onClose} testId="bank-form-drawer">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><label className={lbl}>Account Name</label><input className={inp} value={f.account_name} onChange={(e) => u('account_name', e.target.value)} placeholder="HDFC Current A/c — Nashik" data-testid="bank-form-name" /></div>
        <div><label className={lbl}>Bank</label><input className={inp} value={f.bank_name} onChange={(e) => u('bank_name', e.target.value)} data-testid="bank-form-bank" /></div>
        <div><label className={lbl}>Branch</label><input className={inp} value={f.branch} onChange={(e) => u('branch', e.target.value)} data-testid="bank-form-branch" /></div>
        <div><label className={lbl}>Account No.</label><input className={inp} value={f.account_number} onChange={(e) => u('account_number', e.target.value)} data-testid="bank-form-accno" /></div>
        <div><label className={lbl}>IFSC</label><input className={inp} value={f.ifsc} onChange={(e) => u('ifsc', e.target.value)} data-testid="bank-form-ifsc" /></div>
        <div><label className={lbl}>Type</label>
          <select className={inp} value={f.account_type} onChange={(e) => u('account_type', e.target.value)} data-testid="bank-form-type">
            <option value="current">Current</option><option value="od">OD</option><option value="cc">CC</option>
          </select>
        </div>
        {['od', 'cc'].includes(f.account_type) && (
          <div><label className={lbl}>Sanctioned Limit (₹)</label><input type="number" className={inp} value={f.sanctioned_limit} onChange={(e) => u('sanctioned_limit', e.target.value)} data-testid="bank-form-limit" /></div>
        )}
        <div><label className={lbl}>{editing ? 'Balance (manual adjust)' : 'Opening Balance (₹)'}</label><input type="number" className={inp} value={f.current_balance} onChange={(e) => u('current_balance', e.target.value)} data-testid="bank-form-balance" /></div>
        <div className="col-span-2 flex items-center gap-4 mt-1">
          <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={!!f.is_default} onChange={(e) => u('is_default', e.target.checked)} data-testid="bank-form-default" /> Default account</label>
          <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={f.is_active !== false} onChange={(e) => u('is_active', e.target.checked)} data-testid="bank-form-active" /> Active</label>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button className="px-4 py-2 text-xs font-display font-bold uppercase border border-slate-300 rounded-sm" onClick={onClose} data-testid="bank-form-cancel">Cancel</button>
        <button className="px-4 py-2 text-xs font-display font-bold uppercase text-white bg-[#1F7A6E] rounded-sm" onClick={save} data-testid="bank-form-save">Save</button>
      </div>
    </Drawer>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function Payments() {
  const [tab, setTab] = useState('payments');
  const [banks, setBanks] = useState([]);
  const [showPayment, setShowPayment] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadBanks = useCallback(() => {
    apiGet('/finance/bank-accounts', { include_inactive: true }).then((d) => setBanks(d?.items || [])).catch(() => {});
  }, []);
  useEffect(() => { loadBanks(); }, [loadBanks]);

  const activeBanks = banks.filter((b) => b.is_active !== false);
  const bump = () => setRefreshKey((k) => k + 1);

  const TABS = [
    { id: 'payments', label: 'Payments', icon: ArrowUpCircle },
    { id: 'receipts', label: 'Receipts', icon: ArrowDownCircle },
    { id: 'banks', label: 'Bank Accounts', icon: Landmark },
  ];

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="payments-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">Finance & Accounts</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2"><Banknote size={26} className="text-[#1F7A6E]" /> Payments & Receipts</h1>
          <p className="text-sm text-slate-600 mt-1">Money movement — vouchers update bank balances on approval. Draft → approve (SoD) → bank effect.</p>
        </div>
        <div className="flex gap-2">
          {tab === 'payments' && (
            <button onClick={() => setShowPayment(true)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold uppercase text-white bg-[#1F7A6E] rounded-sm" data-testid="new-payment-btn">
              <Plus size={13} /> New Payment
            </button>
          )}
          {tab === 'receipts' && (
            <button onClick={() => setShowReceipt(true)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold uppercase text-white bg-[#1F7A6E] rounded-sm" data-testid="new-receipt-btn">
              <Plus size={13} /> New Receipt
            </button>
          )}
        </div>
      </div>

      <div className="flex border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-display font-bold border-b-2 -mb-px transition-colors ${tab === t.id ? 'border-[#1F7A6E] text-[#1F7A6E]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${t.id}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'payments' && <VoucherTable kind="payment" refreshKey={refreshKey} banksReload={loadBanks} />}
      {tab === 'receipts' && <VoucherTable kind="receipt" refreshKey={refreshKey} banksReload={loadBanks} />}
      {tab === 'banks' && <BankTab banks={banks} reload={loadBanks} />}

      {showPayment && <PaymentDrawer banks={activeBanks} onClose={() => setShowPayment(false)} onSaved={() => { setShowPayment(false); bump(); }} />}
      {showReceipt && <ReceiptDrawer banks={activeBanks} onClose={() => setShowReceipt(false)} onSaved={() => { setShowReceipt(false); bump(); }} />}
    </div>
  );
}
